import { CallState } from "@/types/callState";
import { Appointment } from "@/types/scheduling/appointment";
import Storage from "@/utils/Storage";
import { useDaily } from "@daily-co/daily-react";
import { useEffect, useRef, useState } from "react";

export const SessionTimeGlobal = {
    elapsed: 0, // in milliseconds
};

const sessionTimeCallbacks: ((elapsed: number) => void)[] = [];

/**
 * countSessionTime() should only be used once in the application, or you end up
 * with multiple counters all mutating the same global state. If you need to get
 * the current time, call getSessionTime() instead. If you need your component to
 * re-render when the time changes, use the useSessionTime() hook.
 */
export function countSessionTime(
    callState: CallState,
    appointmentId: Appointment["id"] | null,
) {
    const dailyCall = useDaily();

    const inActiveSession = useRef(false);
    const lastSave = useRef(Date.now());

    const tick = () => {
        if (!dailyCall || !appointmentId) return;
        const storageKey = `video-timer-${appointmentId}`;
        const storedTime = Storage.load(storageKey, 0);
        SessionTimeGlobal.elapsed = storedTime;
        const now = Date.now();
        if (inActiveSession.current) {
            SessionTimeGlobal.elapsed = now - lastSave.current + storedTime;
            Storage.save(storageKey, SessionTimeGlobal.elapsed, {
                secondsTillExpiry: 60 * 60,
            });
            lastSave.current = now;
            sessionTimeCallbacks.forEach((cb) => cb(SessionTimeGlobal.elapsed));
        }
    };

    const participantChangeHandler = () => {
        if (!dailyCall) {
            inActiveSession.current = false;
            return;
        }
        const callIsInSession = dailyCall.participantCounts().present >= 2;
        if (callIsInSession && !inActiveSession.current) {
            inActiveSession.current = true;
            lastSave.current = Date.now();
        } else if (!callIsInSession) {
            inActiveSession.current = false;
        }
    };

    useEffect(() => {
        participantChangeHandler();
        if (!dailyCall || callState !== CallState.InCall) return;
        dailyCall.on("participant-joined", participantChangeHandler);
        dailyCall.on("participant-left", participantChangeHandler);
        const interval = window.setInterval(() => tick(), 1_000);
        return () => {
            dailyCall.off("participant-joined", participantChangeHandler);
            dailyCall.off("participant-left", participantChangeHandler);
            window.clearInterval(interval);
        };
    }, [dailyCall, appointmentId, callState]);

    return SessionTimeGlobal;
}

export function getSessionTime() {
    return SessionTimeGlobal.elapsed;
}

export function useSessionTime() {
    const [elapsed, setElapsed] = useState(SessionTimeGlobal.elapsed);

    useEffect(() => {
        const cb = (newElapsed: number) => setElapsed(newElapsed);
        sessionTimeCallbacks.push(cb);
        return () => {
            const index = sessionTimeCallbacks.indexOf(cb);
            if (index >= 0) sessionTimeCallbacks.splice(index, 1);
        };
    }, []);

    return elapsed;
}

/**
 * Use this hook to trigger an event after a certain amount of real session time has elapsed.
 * The hook is smart and will not repeat the event even if the users leaves and rejoins or
 * reloads the page.
 */
export function useSessionTimeEvent(
    key: string,
    appointmentId: Appointment["id"] | null,
    threshold: number,
    eventCallback: () => void,
) {
    useEffect(() => {
        if (!appointmentId) return;
        const storageKey = `video-timer-event-${key}-${appointmentId}`;
        const alreadyTriggered = Storage.load(storageKey, false);

        const cb = (newElapsed: number) => {
            if (newElapsed >= threshold && !alreadyTriggered) {
                Storage.save(storageKey, true, {
                    secondsTillExpiry: 60 * 60 * 2 /* 2 hours */,
                });
                cleanup();
                eventCallback();
            }
        };

        const cleanup = () => {
            const index = sessionTimeCallbacks.indexOf(cb);
            if (index >= 0) sessionTimeCallbacks.splice(index, 1);
        };

        sessionTimeCallbacks.push(cb);

        return cleanup;
    }, [key, appointmentId, threshold, eventCallback]);
}
